import React, { FC, useEffect, useRef, useState } from 'react'
import { searchArrayOfObjects } from 'lib/search'
import CloseIcon from 'svg/SA/close-small.svg'
import styles from './Layout.module.scss'
import { SearchInput } from 'components/SA/SearchInput'
import Skeleton from 'components/SA/Skeleton'
import CollectionListItem from './CollectionListItem'
import { SearchInputApi } from 'components/SA/SearchInput/SearchInput'
import useQA from 'hooks/useQA'

type CollectionsListProps = {
  artworkId: number
  collectionList: ReadonlyArray<Record<string, any>>
  collectionsLoaded?: boolean
  artworkList: ReadonlyArray<number>
  handleOpenCreateCollectionsModal: () => void
  onClose: () => void
  onRefetchCollectionsData?: () => Promise<void>
}

const CollectionItemSkeleton = () => {
  return Array.from({ length: 4 }, (_, index) => (
    <Skeleton key={index} width='100%' height='64px' />
  ))
}

const CollectionsList: FC<CollectionsListProps> = ({
  artworkId,
  artworkList,
  collectionList,
  handleOpenCreateCollectionsModal,
  onClose,
  collectionsLoaded = true,
  onRefetchCollectionsData,
}) => {
  const { testId } = useQA()
  const [listOfCollections, setListOfCollections] = useState<ReadonlyArray<Record<string, any>>>([])
  const [isUpdatingCollectionList, setIsUpdatingCollectionList] = useState<boolean>(false)
  const searchInputRef = useRef<SearchInputApi>(null)

  const filterCollectionsBySearchQuery = (query: string) => {
    setListOfCollections(searchArrayOfObjects(query, collectionList, ['title']))
  }

  useEffect(() => {
    filterCollectionsBySearchQuery(searchInputRef.current?.query || '')
  }, [collectionList, searchInputRef.current?.query])

  return (
    <div className={styles.addToCollectionWrapper}>
      <div className={styles.headerContainer}>
        <h4>Add to a Collection</h4>
        <button onClick={onClose}>
          <CloseIcon {...testId('add-to-collection-close-icon')} />
        </button>
      </div>
      {collectionList.length === 0 ? (
        <h6>You have no collections.</h6>
      ) : (
        <>
          <div className={styles.searchContainer}>
            <SearchInput
              ref={searchInputRef}
              placeholder='Search'
              onChange={filterCollectionsBySearchQuery}
            />
          </div>

          <div className={styles.collectionsListWrapper}>
            <>
              <div
                className={styles.collectionsListContainer}
                {...testId('collections-list-container')}
              >
                {!collectionsLoaded ? (
                  <CollectionItemSkeleton />
                ) : (
                  listOfCollections.map(({ title, userCollectionId, artworksCount }) => (
                    <CollectionListItem
                      title={title}
                      artworkId={artworkId}
                      artworksCount={artworksCount}
                      userCollectionId={userCollectionId}
                      artworkList={artworkList}
                      setIsUpdatingCollectionList={setIsUpdatingCollectionList}
                      onRefetchCollectionsData={onRefetchCollectionsData}
                    />
                  ))
                )}
              </div>
              {isUpdatingCollectionList && (
                <div className={styles.loadingOverlay}>
                  <div className={styles.spinner} />
                </div>
              )}
            </>
          </div>
        </>
      )}
      <div
        className={styles.createCollectionButtonContainer}
        {...testId('create-collection-button-container')}
      >
        <button className='defaultButton' onClick={handleOpenCreateCollectionsModal}>
          Create a New Collection
        </button>
      </div>
    </div>
  )
}

export default CollectionsList
